import React, { Component, ReactNode } from 'react';

import { Sentry } from '@//infra/observability/sentry/instance';
import { BreadcrumbCategory } from '@//infra/observability/sentry/types/breadcrumb';
import { FeaturesRequest } from '@//infra/observability/sentry/types/features';
import { addBreadcrumb } from '@//infra/observability/sentry/utils/breadcrumb';
import { captureException } from '@//infra/observability/sentry/utils/exception';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureException(
      error,
      FeaturesRequest.SYSTEM,
      {
        componentStack: errorInfo.componentStack,
      },
      'error',
    );

    this.props.onError?.(error, errorInfo);

    addBreadcrumb(`Error caught by boundary: ${error.message}`, 'error', BreadcrumbCategory.CUSTOM);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='flex min-h-screen items-center justify-center bg-gray-50'>
          <div className='max-w-md rounded-lg bg-white p-6 shadow-lg'>
            <div className='mb-4 flex items-center'>
              <div className='rounded-full bg-red-100 p-2'>
                <svg
                  className='h-6 w-6 text-red-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
              </div>
              <h2 className='ml-3 text-lg font-semibold text-gray-900'>Algo deu errado</h2>
            </div>

            <p className='mb-4 text-sm text-gray-600'>
              Ocorreu um erro inesperado. Nossa equipe foi notificada e está trabalhando para
              corrigir o problema.
            </p>

            <div className='flex space-x-3'>
              <button
                onClick={() => window.location.reload()}
                className='flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700'
              >
                Recarregar página
              </button>

              <button
                onClick={() => this.setState({ hasError: false })}
                className='flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
              >
                Tentar novamente
              </button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className='mt-4'>
                <summary className='cursor-pointer text-sm font-medium text-gray-700'>
                  Detalhes do erro (desenvolvimento)
                </summary>
                <pre className='mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs text-gray-800'>
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function useErrorHandler() {
  return (error: Error, context?: Record<string, unknown>) => {
    Sentry.captureException(error, {
      tags: {
        errorHandler: true,
      },
      extra: context,
    });
  };
}
